import { type Reseller } from '@/wp-pages/blocks/way-out-west/map';
import { FormWOWValues } from '@/wp-pages/blocks/way-out-west/schema';
import { useMutation, useQuery } from '@tanstack/react-query';

const BASE_URL = process.env.KIA_WOW_API;
if (!BASE_URL) throw new Error('Missing required env var: KIA_WOW_API');

export class ApiError extends Error {
  constructor(
    public readonly data: unknown,
    public readonly status: number,
  ) {
    super((data as { message?: string } | null)?.message ?? 'Request failed');
  }
}

type RawReseller = {
  id: number;
  name: string;
  lat: string | number | null;
  lon: string | number | null;
};

function toReseller(r: RawReseller): Reseller | null {
  const lat = Number(r.lat);
  const lon = Number(r.lon);
  if (!r.lat || !r.lon || isNaN(lat) || isNaN(lon)) return null;
  return { id: r.id, name: r.name, lat, lon };
}

export function useResellers() {
  return useQuery({
    queryKey: ['resellers'],
    queryFn: async (): Promise<Reseller[]> => {
      const response = await fetch(`${BASE_URL}/resellers`);
      const data: RawReseller[] = await response.json();
      const resellers = data
        .map(toReseller)
        .filter((r): r is Reseller => r !== null);
      if (
        process.env.VERCEL_ENV === 'preview' ||
        process.env.VERCEL_ENV === 'development'
      ) {
        return resellers.filter((r) => r.id === 97);
      }
      return resellers.filter((r) => r.id !== 97);
    },
  });
}
export function useContribution() {
  return useMutation({
    mutationFn: async (body: FormWOWValues & { recaptcha_token: string }) => {
      const formData = new FormData();
      for (const [key, value] of Object.entries(body)) {
        if (value !== undefined && value !== null) {
          formData.append(key, value as string | Blob);
        }
      }
      const response = await fetch(`${BASE_URL}/contribution`, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const data: unknown = await response.json().catch(() => null);
      if (!response.ok) throw new ApiError(data, response.status);
      return data;
    },
  });
}
